/** @jsxImportSource @emotion/react */
import TileLayer from 'ol/layer/Tile';
import * as s from './style'
import React, { useEffect, useRef, useState } from 'react';
import { ImageWMS, XYZ } from 'ol/source';
import { Map, View } from 'ol';
import VectorLayer from 'ol/layer/Vector';
import axios from 'axios';
import { GEOSERVER } from 'config/URL/URL';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Select from 'ol/interaction/Select';
import { click } from 'ol/events/condition';
import { WKT } from 'ol/format';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts'
import { Fill, Stroke, Style } from 'ol/style';
import SidemenuLayerItem from './layer_item/SidemenuLayerItem';
import ImageLayer from 'ol/layer/Image';


const Amount = () => {
    const mapRef = useRef();
    const layers = [
        {
            id: 'bjd_layer',
            name: '법정동 레이어',
            layerType: 'POLYGON'
        },
        {
            id: 'land_layer',
            name: '개별공시지가 레이어',
            layerType: 'POLYGON'
        },
        {
            id: 'sohou_layer',
            name: '개별주택가격 레이어',
            layerType: 'POINT'
        },
        {
            id: 'mohou_layer',
            name: '공동주택가격 레이어',
            layerType: 'POINT'
        },
    ]

    const [ data, setData ] = useState();
    const [ summaryData, setSummaryData ] = useState();
    const [ chartOption, setChartOption ] = useState();

    const [ isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        initMap();
        Highcharts.setOptions({ lang : {thousandsSep:','} });
    }, [])

    useEffect(() => {
        if(data) {
            calcData(data);
        }
    }, [data])

    useEffect(() => {
        setChartOption({
            title: { text: "평균 면적당 가격" },
            chart: {
                backgroundColor: "rgba(0, 0, 0, 0)",
                height: 250,
                type: "column",
                style: { fontFamily: "SCDream4" },
            },
            xAxis: {
                labels: { style: { fontSize: "14px", fontWeight: 400, color: "#002755" } },
                categories: ['개별공시지가', '개별주택가격', '공동주택가격']
            },
            yAxis: { labels: { enabled: false }, title: { text: "" } },
            legend: { itemStyle: { fontSize: "13px", fontWeight: 700 } },
            credits: { enabled: false },
            series: [
                {
                    name: '2023',
                    data: summaryData ? ['land', 'sohou', 'mohou'].map(k => summaryData[k].avg[2023]) : []
                },
                {
                    name: '2024',
                    data: summaryData ? ['land', 'sohou', 'mohou'].map(k => summaryData[k].avg[2024]) : []
                },
                {
                    name: '2025',
                    data: summaryData ? ['land', 'sohou', 'mohou'].map(k => summaryData[k].avg[2025]) : []
                },
            ],
            accessibility: { enabled: false },
        })
    }, [summaryData])

    const initMap = async () => {
        if(!mapRef.current) {
            const vworldLayer = new TileLayer({
                title: 'VWorld white Map',
                visible: true,
                type: 'white',
                source: new XYZ({
                    url: 'https://xdworld.vworld.kr/2d/white/service/{z}/{x}/{y}.png'
                }),
                zIndex: 1
            });

            const landLayer = new ImageLayer({
                properties: {
                    layerName: 'land_layer'
                },
                source: new ImageWMS({
                    url: GEOSERVER + '/geoserver/wms',
                    params: { 'LAYERS': 'gis_solution:tb_land_amount' },
                    ratio: 1,
                    serverType: 'geoserver'
                }),
                zIndex: 80,
                visible: false
            });

            const sohouLayer = new ImageLayer({
                properties: {
                    layerName: 'sohou_layer'
                },
                source: new ImageWMS({
                    url: GEOSERVER + '/geoserver/wms',
                    params: { 'LAYERS': 'gis_solution:tb_sohou_amount' },
                    ratio: 1,
                    serverType: 'geoserver'
                }),
                zIndex: 85,
                visible: false
            });

            const mohouLayer = new ImageLayer({
                properties: {
                    layerName: 'mohou_layer'
                },
                source: new ImageWMS({
                    url: GEOSERVER + '/geoserver/wms',
                    params: { 'LAYERS': 'gis_solution:tb_mohou_amount' },
                    ratio: 1,
                    serverType: 'geoserver'
                }),
                zIndex: 85,
                visible: false
            })

            const bjdSource = new VectorSource();

            const bjdLayer = new VectorLayer({
                properties: {
                    layerName: 'bjd_layer'
                },
                source: bjdSource,
                zIndex: 90
            })

            const data = await getDataLayerWFSSource();

            const features = new GeoJSON().readFeatures(data, {
                dataProjection: 'EPSG:3857',
                featureProjection: 'EPSG:3857',
            });

            bjdSource.addFeatures(features)

            const view = new View({
                center: [14345127.538594436, 4197866.877180552],
                zoom: 11.5,
                maxZoom: 18,
                minZoom: 7
            });

            const map = new Map({
                layers: [vworldLayer, bjdLayer, landLayer, sohouLayer, mohouLayer],
                target: "map_area",
                view: view
            });

            const select = new Select({
                condition: click,
                layers: [bjdLayer],
                style: new Style({
                    fill: new Fill({
                        color: 'rgba(244, 164, 96, 0.5)',
                    }),
                    stroke: new Stroke({
                        color: '#FF8C00',
                        width: 3
                    }),
                    zIndex: 99
                }),
            })

            map.addInteraction(select);

            select.on('select', async (e) => {
                if (isLoading) return;
                setIsLoading(true)

                map.getInteractions().forEach(i => i.setActive(false));

                try {
                    const selected = e.selected?.[0]
                    const sohouFeatures = await fetchFeaturesInSelectedArea(selected, 'tb_sohou_amount', ['amount', 'area', 'base_year']);
                    const mohouFeatures = await fetchFeaturesInSelectedArea(selected, 'tb_mohou_amount', ['amount_area', 'base_year']);
                    const landFeatures = await fetchFeaturesInSelectedArea(selected, 'tb_land_amount', ['amount_area', 'base_year']);
    
                    sohouFeatures?.features.forEach((feature) => {
                        const { amount, area } = feature.properties;
                        feature.properties.amount_area = amount / area
                    })
    
                    const resultSohou = devideDataByYear(sohouFeatures.features);
                    const resultMohou = devideDataByYear(mohouFeatures.features);
                    const resultLand = devideDataByYear(landFeatures.features);
    
                    setData({ sohou: resultSohou, mohou: resultMohou, land: resultLand });
                } finally {
                    map.getInteractions().forEach(i => i.setActive(true));
                    setIsLoading(false);
                }
            });

            setIsLoading(true);

            map.once('rendercomplete', () => {
                setIsLoading(false);
            });

            mapRef.current = map;
        }
    }

    const fetchFeaturesInSelectedArea = async (selectedFeature, layerName, propertyNames) => {
        const mapSrs = 'EPSG:3857';
        const dataSrs = 'EPSG:5186';

        const geom = selectedFeature.getGeometry().clone();
        const transedGeom = geom.transform(mapSrs, dataSrs);

        const wkt = new WKT().writeGeometry(transedGeom);

        const cql = `INTERSECTS(geom, SRID=5186;${wkt})`;

        const body = new URLSearchParams({
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: `gis_solution:${layerName}`,
            outputFormat: 'application/json',
            srsName: dataSrs,
            cql_filter: cql,
            propertyName: propertyNames.join(','),
        });

        const res = await axios.post(
            `${GEOSERVER}/geoserver/gis_solution/ows`,
            body,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
            }
        );
        return res.data;
    }

    const getDataLayerWFSSource = async () => {
        const option = {
            params: {
                service: 'WFS',
                version: '1.3.0',
                request: 'GetFeature',
                typeName: `gis_solution:tl_bjd_emd_gimhae`,
                maxFeatures: 100,
                outputFormat: 'application/json'
            }
        };

        try {
            const response = await axios.get(`${GEOSERVER}/geoserver/gis_solution/ows`, option);
            return response.data
        } catch(error) {
            console.log(error)
        }
    }

    const devideDataByYear = (rawData) => {
        return rawData?.reduce((acc, f) => {
            const year = f?.properties?.base_year;
            if (!year) return acc;

            if (!acc[year]) acc[year] = [];
            
            acc[year].push(f);

            return acc;
        }, {});
    }

    const calcData = (sortData) => {
        const result = {};

        Object.entries(sortData).forEach(([groupName, yearObj]) => {
            const labelMap = {
                land: '개별공시지가',
                sohou: '개별주택가격',
                mohou: '공동주택가격'
            };

            const stats = {
                avg: {}, 
                min: {}, 
                max: {}
            };

            Object.entries(yearObj).forEach(([year, features]) => {
                const values = features.filter(f => f?.properties?.amount_area != null).map(f => Number(f?.properties?.amount_area)).filter(v => Number.isFinite(v));

                if (values.length === 0) {
                    stats.avg[year] = null;
                    stats.min[year] = null;
                    stats.max[year] = null;
                    return;
                }

                const sum = values.reduce((a, b) => a + b, 0);
                stats.avg[year] = Math.round(sum / values.length);
                stats.min[year] = Math.round(Math.min(...values));
                stats.max[year] = Math.round(Math.max(...values));
            })

            result[groupName] = stats;
        });

        setSummaryData(result);
    }

    return (
        <div css={s.mainContainer}>
            <div css={s.sidemenuContainer}>
                <div css={s.sidemenuContentContainer}>
                    {
                        layers.map((layer) => (
                            <SidemenuLayerItem key={layer.id} data={layer} map={mapRef.current} isLoading={isLoading}/>
                        ))
                    }
                </div>
            </div>
            <div css={s.mapContainer}>
                <div css={s.mapArea} id="map_area"><div />
            </div>
            </div>
            <div css={s.widgetContainer}>
                <div css={s.sidemenuContentArea}>
                    <div css={s.unit}>
                        <p>(단위: 원/㎡)</p>
                    </div>
                    <div css={s.sidemenuContent}>
                        <table css={s.table}>
                            <thead css={s.thead}>
                                <tr>
                                    <th colSpan='2' css={s.thAndTd}>구분</th>
                                    <th css={s.thAndTd}>2023</th>
                                    <th css={s.thAndTd}>2024</th>
                                    <th css={s.thAndTd}>2025</th>
                                </tr>
                            </thead>
                            <tbody css={s.tbody}>
                                {[
                                    { key: 'land',  label: '개별공시지가' },
                                    { key: 'sohou', label: '개별주택가격' },
                                    { key: 'mohou', label: '공동주택가격' }
                                ].map(({ key, label }) => (
                                    ['avg', 'max', 'min'].map((type, i) => (
                                    <tr key={`${key}-${type}`}>
                                        {i === 0 && (
                                        <td rowSpan="3" css={s.thAndTd}>{label}</td>
                                        )}
                                        <td css={s.thAndTd}>
                                        {type === 'avg' ? '평균' : type === 'max' ? '최대' : '최소'}
                                        </td>
                                        {[2023, 2024, 2025].map(y => (
                                        <td key={y} css={s.val}>
                                            {
                                                summaryData?.[key]?.[type]?.[y] != null
                                                    ? summaryData[key][type][y].toLocaleString()
                                                    : '-'
                                            }
                                        </td>
                                        ))}
                                    </tr>
                                    ))
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div css={s.sidemenuContent}>
                        <HighchartsReact highcharts={Highcharts} options={chartOption}/>
                    </div>
                </div>
            </div>
            {isLoading && (
                <div css={s.loadingOverlay}>
                    <div css={s.spinner} />
                </div>
            )}       
        </div>
    );
};

export default Amount;